import { FeedEntry, Medium } from "protogen/feed_pb";
import { TaskChangeEventType } from "protogen/tasks_pb";
import { DocumentType } from "protogen/documents_pb";

export interface GroupedEntry {
  actorRef: string;
  type: string;
  typeRef: string;
  entries: FeedEntry[];
}

export const getActivityLink = (entry: FeedEntry): string => {
  if (entry.agentConversation?.conversationRef) {
    const ref = encodeURIComponent(entry.agentConversation.conversationRef);
    if (
      entry.medium === Medium.SMS_BOT ||
      entry.medium === Medium.LINQ_IMESSAGE
    ) {
      return `/conversations/sms/${ref}`;
    }
    return `/conversations/in-app/${ref}`;
  }
  if (entry.agentCron) {
    return `/scheduled-reminders`;
  }
  if (entry.task?.ref) {
    const blockRef = entry.taskBlock?.ref;
    const scrollParam = blockRef
      ? `?blockRef=${encodeURIComponent(blockRef)}`
      : "";
    return `/tasks/${encodeURIComponent(entry.task?.ref)}${scrollParam}`;
  }
  if (entry.secretName) {
    return `/secret/${encodeURIComponent(entry.ref)}`;
  }
  if (entry?.document?.ref) {
    if (entry.document.documentType === DocumentType.FORM) {
      return `/forms/complete/${entry.document.uuid}`;
    }
    return `/documents/${entry.document.uuid}`;
  }
  if (entry.emailMessage) {
    return `/emails/${encodeURIComponent(entry.emailMessage.ref)}`;
  }
  return "";
};

export const getActivityTitle = (entry: FeedEntry): string => {
  if (entry.agentConversation) {
    return entry.agentConversation.title || "Conversation";
  }
  if (entry.agentCron?.title) {
    return entry.agentCron.title;
  }
  if (entry.task?.title) {
    return entry.task.title;
  }
  if (entry.secretName) {
    return entry.secretName;
  }
  if (entry?.document?.title) {
    return entry.document.title;
  }
  if (entry.emailMessage?.subject) {
    return entry.emailMessage.subject;
  }
  return "Unknown task";
};

const getEmailActionDescription = (
  entry: FeedEntry,
  displayName: string,
): string => {
  // Email-specific descriptions based on the action type
  const toAddress = entry.emailMessage?.toAddresses?.[0] || "";
  const fromAddress = entry.emailMessage?.fromAddress || "";
  if (entry.emailMessage?.emailType === "tasks") {
    return `${displayName} added tasks from an email`;
  } else if (entry.emailMessage?.emailType === "events") {
    return `${displayName} added new events from an email`;
  } else if (entry.emailMessage?.emailType === "docs") {
    return `${displayName} added content from an email`;
  } else if (entry.emailMessage?.emailType === "family-alias") {
    return `Email received from ${fromAddress}`;
  }
  return `${displayName} sent an email to ${toAddress}`;
};

const getEmailByline = (entry: FeedEntry): string | null => {
  // Email-specific descriptions based on the action type
  const toAddress = entry.emailMessage?.toAddresses?.[0] || "";
  return toAddress;
};

export const getActvityByline = (entry: FeedEntry): string | null => {
  if (entry.emailMessage) {
    return getEmailByline(entry);
  }
  return null;
};

export const getActivityDescription = (
  entry: FeedEntry,
  displayName: string,
): string => {
  if (entry.agentConversation) {
    return "Conversation updated";
  }
  if (entry.agentCron) {
    return `${displayName} set up a scheduled reminder`;
  }
  // Handle specific activity types first
  if (entry.secretName) {
    return `${displayName} added a new secret`;
  }
  if (entry?.document?.ref) {
    return `${displayName} added a new document`;
  }
  if (entry.emailMessage) {
    return getEmailActionDescription(entry, displayName);
  }

  const hasAction = !!entry.taskChangeEvent;
  const hasComment = !!entry.taskBlock;
  const event = entry.taskChangeEvent;

  // Get the base action description
  let actionDescription = "";
  if (hasAction && event) {
    switch (event.type) {
      case TaskChangeEventType.TASK_CHANGE_EVENT_CREATED:
        actionDescription = "created a new task";
        break;
      case TaskChangeEventType.TASK_CHANGE_EVENT_COMPLETED:
        actionDescription = "completed a task";
        break;
      case TaskChangeEventType.TASK_CHANGE_EVENT_DELETED:
        actionDescription = "deleted a task";
        break;
      case TaskChangeEventType.TASK_CHANGE_EVENT_STAGE_CHANGED:
        actionDescription = hasComment
          ? "updated a task"
          : `updated status to ${event.newStage}`;
        break;
      default:
        actionDescription = "performed an action";
    }
  }

  // Combine with comment if present
  if (hasAction && hasComment) {
    return actionDescription || "performed an action and left a comment";
  }
  if (hasAction) {
    return `${displayName} ${actionDescription || "performed an action"}`;
  }
  if (hasComment) {
    return `${displayName} ${
      hasComment ? "left a comment" : "performed an action"
    }`;
  }
  return `${displayName} ${
    hasAction ? "performed an action" : "left a comment"
  }`;
};

export const getCommentPreview = (
  entry: FeedEntry,
  maxLength: number = 150,
): string | null => {
  if (entry.taskBlock?.textContent) {
    const textContent = entry.taskBlock.textContent.replace(/<[^>]*>/g, "");
    return textContent.length > maxLength
      ? `${textContent.substring(0, maxLength)}...`
      : textContent;
  }
  return null;
};

export const combineEntries = (entries: FeedEntry[]): FeedEntry[] => {
  const combinedEntries: FeedEntry[] = [];
  const processedIndices = new Set<number>();

  for (let i = 0; i < entries.length; i++) {
    if (processedIndices.has(i)) continue;

    const currentEntry = entries[i];
    let combinedEntry = currentEntry.clone();

    // Look for a matching entry to combine with
    for (let j = i + 1; j < entries.length; j++) {
      if (processedIndices.has(j)) continue;

      const nextEntry = entries[j];

      // Check if entries should be combined:
      // 1. Same user
      // 2. Same task
      // 3. Same timestamp (within 1 second)
      // 4. One has action, other has comment
      const sameUser = currentEntry.senderRef === nextEntry.senderRef;
      const sameTask =
        !!currentEntry.task?.ref &&
        currentEntry.task?.ref === nextEntry.task?.ref;
      const sameTime =
        Math.abs(
          Number(currentEntry.timestampSec) - Number(nextEntry.timestampSec),
        ) <= 1;
      const hasAction =
        currentEntry.taskChangeEvent || nextEntry.taskChangeEvent;
      const hasComment = currentEntry.taskBlock || nextEntry.taskBlock;
      const differentTypes =
        (currentEntry.taskChangeEvent && nextEntry.taskBlock) ||
        (currentEntry.taskBlock && nextEntry.taskChangeEvent);

      if (
        sameUser &&
        sameTask &&
        sameTime &&
        hasAction &&
        hasComment &&
        differentTypes
      ) {
        // Combine the entries
        if (nextEntry.taskChangeEvent && !combinedEntry.taskChangeEvent) {
          combinedEntry.taskChangeEvent = nextEntry.taskChangeEvent;
        }
        if (nextEntry.taskBlock && !combinedEntry.taskBlock) {
          combinedEntry.taskBlock = nextEntry.taskBlock;
        }
        processedIndices.add(j);
        break;
      }
    }

    combinedEntries.push(combinedEntry);
    processedIndices.add(i);
  }

  return combinedEntries;
};

export const groupEntriesByActorAndType = (
  entries: FeedEntry[],
): GroupedEntry[] => {
  const groupedEntries: GroupedEntry[] = [];
  let currentGroup: GroupedEntry | null = null;

  entries.forEach((entry) => {
    if (
      !currentGroup ||
      currentGroup.actorRef !== entry.senderRef ||
      currentGroup.type !== entry.medium.toString() ||
      currentGroup.typeRef !== entry.ref
    ) {
      // Start a new group
      currentGroup = {
        actorRef: entry.senderRef,
        type: entry.medium.toString(),
        typeRef: entry.ref,
        entries: [entry],
      };
      groupedEntries.push(currentGroup);
    } else {
      // Add to current group
      currentGroup.entries.push(entry);
    }
  });

  return groupedEntries;
};
